<template>
  <div class="page-container">
    <!-- Back Link -->
    <div class="back-link-section">
      <router-link to="/all-skiers" class="back-link">
        <span class="arrow">&#8592;</span>
        <span class="back-link-text">View All Skiers</span>
      </router-link>
    </div>

    <!-- Profile Section -->
    <ProfileContainer 
      v-if="profile.username"
      :profile="profile"
      :isAuthenticated="isAuthenticated"
      :userId="user.id"
      :avatar="avatarPreview || profile.avatar"
    />

    <!-- Toggle Buttons for Posts vs. Comments vs. Paid Tips -->
    <div class="toggle-buttons">
      <button
        :class="{ active: viewType === 'posts' }"
        @click="viewType = 'posts'"
      >
        Posts
      </button>
      <button
        :class="{ active: viewType === 'comments' }"
        @click="viewType = 'comments'"
      >
        Tips Received
      </button>
      <button
        :class="{ active: viewType === 'tipsProvided' }"
        @click="viewType = 'tipsProvided'"
      >
        Tips Provided
      </button>
      <button
        v-if="isAuthenticated && user.id === profile.id"
        :class="{ active: viewType === 'paidTipsFromPros' }"
        @click="viewType = 'paidTipsFromPros'"
      >
        Pro Tips
      </button>
    </div>

    <!-- POSTS SECTION -->
    <div v-if="viewType === 'posts'" class="timeline">
      <LoadingSpinner v-if="loadingPosts" />
      <template v-else>
        <PostCard 
          v-for="post in posts" 
          :key="post.id" 
          :post="post"
          :shared-user-id="user.id"
          :shared-user-votes="userVotes"
          :ref="(el) => { if (el) postCards[post.id] = el }"
          @tips-count-updated="(count) => updateTipsCount(post.id, count)"
          @like-updated="(likeData) => updateLike(post.id, likeData)"
        />
      </template>
    </div>

    <!-- COMMENTS SECTION (Rating + Replies) -->
    <div v-else-if="viewType === 'comments'" class="comments-section">
      <LoadingSpinner v-if="loadingComments" />
      <WaterskiTips 
        v-else
        :post-id="profile.id"
        :initial-tips="comments"
        :show-new-tip-form="false"
        @update:tips-count="updateCommentsCount"
      />
    </div>

    <!-- TIPS PROVIDED SECTION -->
    <div v-else-if="viewType === 'tipsProvided'" class="tips-provided-section">
      <!-- Sub-toggle for Students vs Tips -->
      <div class="sub-toggle-buttons">
        <button
          :class="{ active: tipsProvidedViewType === 'students' }"
          @click="tipsProvidedViewType = 'students'"
        >
          Students
        </button>
        <button
          :class="{ active: tipsProvidedViewType === 'tips' }"
          @click="tipsProvidedViewType = 'tips'"
        >
          Tips
        </button>
      </div>

      <!-- Students View -->
      <div v-if="tipsProvidedViewType === 'students'" class="students-section">
        <LoadingSpinner v-if="loadingStudents" />
        <div v-else>
          <div v-if="students.length === 0" class="no-students-message">
            <p>No students yet</p>
          </div>
          <div v-else class="students-list">
            <div v-for="studentData in students" :key="studentData.student.id" class="student-item">
              <router-link :to="`${studentData.student.username}`" class="student-link">
                <img
                  :src="studentData.student.avatar || defaultAvatar"
                  :alt="studentData.student.username"
                  class="student-avatar"
                />
                <div class="student-info">
                  <span class="student-username">{{ studentData.student.username }}</span>
                  <span class="student-tip-count">{{ studentData.tip_count }} tips</span>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Tips View -->
      <div v-else-if="tipsProvidedViewType === 'tips'">
        <LoadingSpinner v-if="loadingTipsProvided" />
        <WaterskiTips 
          v-else
          :post-id="profile.id"
          :initial-tips="tipsProvided"
          :show-new-tip-form="false"
          @update:tips-count="updateTipsProvidedCount"
        />
      </div>
    </div>

    <!-- PAID TIPS FROM PROS SECTION -->
    <div v-else-if="viewType === 'paidTipsFromPros'" class="tips-from-pros">
      <LoadingSpinner v-if="loadingPaidTips" />
      <div v-else>
        <!-- If no paid tips, show a message -->
        <div v-if="paidTipsFromPros.length === 0" class="no-tips-message">
          <p>No Paid Tips from Pro Skiers yet</p>
          <button class="request-tip-button" @click="goToRequestProTip">
            Request Your First Pro Tip
          </button>
        </div>

        <!-- Otherwise list them -->
        <ul v-else class="paid-tips-list">
        <li v-for="payment in paidTipsFromPros" :key="payment.id" class="payment-item">
            <!-- First Row: Profile picture and status/price -->
            <div class="first-row">
            <div class="pro-info">
                <img
                :src="payment.pro_avatar || defaultAvatar"
                class="pro-avatar"
                alt="Pro Avatar"
                />
                <span class="pro-username">
                {{ payment.pro_username }}
                </span>
            </div>
            <div class="payment-details">
                <div class="payment-status">
                <strong>Status:</strong> {{ payment.status }}
                </div>
                <div class="payment-price">
                <strong>Price:</strong> ${{ payment.amount }}
                </div>
            </div>
            </div>

            <!-- Second Row: Video preview or content -->
            <div class="second-row">
            <div class="post-info" v-if="payment.post_preview">
                <img
                v-if="payment.post_preview.image"
                :src="payment.post_preview.image"
                alt="Post image"
                class="post-thumbnail"
                />
                <video
                v-if="payment.post_preview.video"
                :src="payment.post_preview.video"
                muted
                autoplay
                loop
                playsinline
                class="post-thumbnail"
                ></video>
                <p class="paid-post-content">{{ payment.post_preview.content }}</p>
            </div>
            </div>
        </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, WEB_BASE_URL } from '@/config';
import ProfileContainer from '@/components/ProfileContainer.vue';
import PostCard from '@/components/PostCard.vue';
import WaterskiTips from '@/components/WaterskiTips.vue';
import axios from "axios";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import { Capacitor } from '@capacitor/core';



export default {
  components: {
    LoadingSpinner,
    ProfileContainer,
    PostCard,
    WaterskiTips
  },
  props: ["username"],
  data() {
    return {
      profile: {
        username: "",
        first_name: "",
        last_name: "",
        bio: "",
        avatar: "",
        posts_count: 0,
        followers_count: 0,
        following_count: 0,
      },
      posts: [],
      comments: [],              // For user-received comments
      tipsProvided: [],          // For user-provided comments
      students: [],              // For user's students
      defaultAvatar: "https://6buoys-bucket.s3.amazonaws.com/avatar.png",
      loadingPosts: true,
      loadingComments: false,
      loadingTipsProvided: false,
      loadingStudents: false,

      /* Authentication & user info */
      user: {},
      isAuthenticated: false,

      /* For toggling: 'posts', 'comments', or 'paidTipsFromPros' */
      viewType: "posts",

      /* For comment voting & replies */
      userVotes: {},            // commentId => { rate: +1/-1, rateId: someNumber }
      newReply: {},             // commentId => 'reply text'

      /* For Paid Tips from Pros */
      paidTipsFromPros: [],
      loadingPaidTips: false,
      paidTipsFromProsLoaded: false,
      tipsProvidedLoaded: false,
      studentsLoaded: false,
      tipsProvidedViewType: "students", // 'students' or 'tips'
      isWeb: false,
      isMobile: false,
      postCards: {},
      loadingMorePosts: false,
      postsLimit: 3,
      postsOffset: 0,
    };
  },
  async created() {
    await this.checkAuthentication();
    await this.fetchProfile();
    
    // Set viewType from query params before fetching data
    const queryViewType = this.$route.query.viewType;
    this.viewType = queryViewType || "posts";

    // Fetch both posts and comments initially
    if (this.isAuthenticated && this.user.id) {
      await Promise.all([
        this.fetchPosts(),
        this.fetchComments()
      ]);
    } else {
      await Promise.all([
        this.fetchPosts(),
        this.fetchComments()
      ]);
    }
    
    if (this.viewType === "paidTipsFromPros" && this.isAuthenticated) {
      await this.fetchPaidTipsFromPros();
    }
    
    const platform = Capacitor.getPlatform();
    this.isWeb = platform === 'web';

    // Check if mobile
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);

    this.setupScrollListener();
  },
  watch: {
    // Whenever 'viewType' changes, load the relevant data if not already loaded
    viewType(newVal) {
      if (newVal === "comments" && this.comments.length === 0) {
        this.fetchComments();
      }
      if (newVal === "tipsProvided") {
        if (!this.tipsProvidedLoaded) {
          this.fetchTipsProvided();
        }
        if (!this.studentsLoaded) {
          this.fetchStudents();
        }
      }
      if (newVal === "paidTipsFromPros" && !this.paidTipsFromProsLoaded) {
        this.fetchPaidTipsFromPros();
      }
    },
    // Watch for changes in the tips provided sub-view
    tipsProvidedViewType(newVal) {
      if (newVal === "students" && !this.studentsLoaded) {
        this.fetchStudents();
      }
      if (newVal === "tips" && !this.tipsProvidedLoaded) {
        this.fetchTipsProvided();
      }
    },
    '$route.query.viewType': function (newVal) {
      this.viewType = newVal || "posts";
      if (this.viewType === "tipsProvided") {
        this.fetchTipsProvided();
        this.fetchStudents();
      } else if (this.viewType === "paidTipsFromPros") {
        this.fetchPaidTipsFromPros();
      }
    },
    '$route.params.username': {
      immediate: true,
      handler: async function(newUsername) {
        if (newUsername) {
          this.loadingPosts = true;
          // Reset posts array and offset when username changes
          this.posts = [];
          this.postsOffset = 0;
          
          await this.checkAuthentication();
          await this.fetchUserVotes();
          await this.fetchProfile();
          await this.fetchPosts();
          
          // Don't reset view type to posts when profile changes
          // Instead, respect the query parameter if it exists
          const queryViewType = this.$route.query.viewType;
          if (queryViewType) {
            this.viewType = queryViewType;
          } else {
            this.viewType = "posts";
          }
          
          // Reset other data as needed
          this.comments = [];
          this.tipsProvided = [];
          this.students = [];
          this.paidTipsFromPros = [];
          this.paidTipsFromProsLoaded = false;
          this.tipsProvidedLoaded = false;
          this.studentsLoaded = false;
          
          // Load the appropriate data based on viewType
          if (this.viewType === "comments") {
            this.fetchComments();
          } else if (this.viewType === "tipsProvided") {
            this.fetchTipsProvided();
            this.fetchStudents();
          } else if (this.viewType === "paidTipsFromPros" && this.isAuthenticated) {
            this.fetchPaidTipsFromPros();
          }
        }
      }
    },
  },
  methods: {
    /* ---------------- AUTH ----------------- */
    async checkAuthentication() {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.user = response.data;
          this.isAuthenticated = true;
        } catch (error) {
          console.error("Error fetching user data:", error);
          this.isAuthenticated = false;
        }
      } else {
        this.isAuthenticated = false;
      }
    },

    // Fetch the current user's comment votes once so each PostCard can reuse
    // them instead of fetching /api/user-comment-rates/ individually.
    async fetchUserVotes() {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      try {
        const votesResponse = await axios.get(`${API_BASE_URL}/api/user-comment-rates/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.userVotes = votesResponse.data.reduce((acc, vote) => {
          acc[vote.comment] = { rate: vote.rate, rateId: vote.id };
          return acc;
        }, {});
      } catch (error) {
        console.error("Error fetching user votes:", error);
      }
    },

    /* ---------------- PROFILE / POSTS ---------------- */
    async fetchProfile() {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/p/${this.username}/`);
        this.profile = response.data;
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    },
    async fetchPosts() {
      try {
        const token = localStorage.getItem("access_token");
        if (!token && !this.isAuthenticated) {
          console.log("Fetching posts without authentication");
        }
        
        if (this.loadingMorePosts) return;
        this.loadingMorePosts = true;
        
        const response = await axios.get(`${API_BASE_URL}/api/posts/${this.profile.id}/`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {},
          params: {
            user_id: this.user.id || null,  // Explicitly pass the user ID
            limit: this.postsLimit,
            offset: this.postsOffset
          }
        });
        
        const newPosts = response.data.map(post => ({
          ...post,
          showTips: false,
          tips: [],
          tips_count: post.tips_count || 0,
          likes_count: post.likes_count || 0,
          liked_by_user: post.liked_by_user || false,
          like_id: post.like_id || null,
          author: post.author || {
            id: null,
            username: '',
            avatar: 'default-avatar.png',
            first_name: 'Unknown',
            last_name: ''
          }
        }));

        // Append new posts to existing posts
        this.posts = [...this.posts, ...newPosts];
        
        // Update offset for next fetch
        this.postsOffset += this.postsLimit;

        // Increment view count for each post
        newPosts.forEach(post => {
          this.incrementPostView(post.id);
        });

        // Tips are preloaded by each PostCard on mount; no need to trigger again here.
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        this.loadingPosts = false;
        this.loadingMorePosts = false;
      }
    },

    /* ---------------- COMMENTS (Coaching Tips) ---------------- */
    async fetchComments() {
      this.loadingComments = true;
      try {
        // Fetch comments
        const response = await axios.get(`${API_BASE_URL}/api/user-received-comments/${this.profile.user_id}/`);
        
        // Fetch user votes if authenticated
        let userVotes = {};
        if (this.isAuthenticated) {
          const votesResponse = await axios.get(`${API_BASE_URL}/api/user-comment-rates/`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          userVotes = votesResponse.data.reduce((acc, vote) => {
            acc[vote.comment] = { rate: vote.rate, rateId: vote.id };
            return acc;
          }, {});
        }

        // Merge comments with user votes
        this.comments = response.data.map(comment => ({
          ...comment,
          userVote: userVotes[comment.id]?.rate || 0,
          rateId: userVotes[comment.id]?.rateId || null
        }));
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        this.loadingComments = false;
      }
    },

    /* ---------------- TIPS PROVIDED ---------------- */
    async fetchTipsProvided() {
      this.loadingTipsProvided = true;
      try {
        // Fetch tips provided by this user
        const response = await axios.get(`${API_BASE_URL}/api/user-provided-comments/${this.profile.user_id}/`);
        
        // Fetch user votes if authenticated
        let userVotes = {};
        if (this.isAuthenticated) {
          const votesResponse = await axios.get(`${API_BASE_URL}/api/user-comment-rates/`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          userVotes = votesResponse.data.reduce((acc, vote) => {
            acc[vote.comment] = { rate: vote.rate, rateId: vote.id };
            return acc;
          }, {});
        }

        // Merge tips provided with user votes
        this.tipsProvided = response.data.map(comment => ({
          ...comment,
          userVote: userVotes[comment.id]?.rate || 0,
          rateId: userVotes[comment.id]?.rateId || null
        }));
        this.tipsProvidedLoaded = true;
      } catch (error) {
        console.error("Error fetching tips provided:", error);
      } finally {
        this.loadingTipsProvided = false;
      }
    },

    /* ---------------- STUDENTS ---------------- */
    async fetchStudents() {
      this.loadingStudents = true;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/user-students/${this.profile.user_id}/`);
        
        // Sort students by tip count in descending order
        this.students = response.data.sort((a, b) => b.tip_count - a.tip_count);
        this.studentsLoaded = true;
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        this.loadingStudents = false;
      }
    },

    /* ---------------- PAID TIPS FROM PROS ---------------- */
    async fetchPaidTipsFromPros() {
      this.loadingPaidTips = true;
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          console.error("No authentication token found");
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/payments/user-payments/${this.profile.user_id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        this.paidTipsFromPros = response.data;
        this.paidTipsFromProsLoaded = true;
      } catch (error) {
        console.error("Error fetching paid tips:", error);
      } finally {
        this.loadingPaidTips = false;
      }
    },
    goToRequestProTip() {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      if (!this.isWeb || this.isMobile) {
        // For mobile apps, open in browser with token
        const state = Math.random().toString(36).substring(2, 15);
        localStorage.setItem('auth_state', state);
        
        const url = `${WEB_BASE_URL}/request-pro-tip?token=${encodeURIComponent(token)}&state=${state}`;
        window.open(url, '_blank');
      } else {
        // For web, navigate internally
        this.$router.push('/request-pro-tip');
      }
    },

    /* ---------------- UTILITIES ---------------- */
    formatDate(date) {
      if (!date) return "";
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(date).toLocaleDateString(undefined, options);
    },
    updateTipsCount(postId, count) {
      const post = this.posts.find(p => p.id === postId);
      if (post) {
        post.tips_count = count;
      }
    },
    updateLike(postId, likeData) {
      const post = this.posts.find(p => p.id === postId);
      if (post) {
        post.liked_by_user = likeData.liked_by_user;
        post.likes_count = likeData.likes_count;
        post.like_id = likeData.like_id;
      }
    },
    updateCommentsCount() {
      // Optionally update the comments count in the profile if needed
      this.fetchProfile();
    },
    updateTipsProvidedCount() {
      // Optionally update the tips provided count in the profile if needed
      this.fetchProfile();
    },
    async incrementPostView(postId) {
      try {
        const token = localStorage.getItem("access_token");
        await axios.post(`${API_BASE_URL}/api/post-view/${postId}/`, {}, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
      } catch (error) {
        console.error(`Error incrementing view count for post ${postId}:`, error);
      }
    },
    setupScrollListener() {
      window.addEventListener('scroll', () => {
        if (
          this.viewType === 'posts' && 
          window.innerHeight + window.scrollY >= document.body.offsetHeight - 500 && 
          !this.loadingMorePosts
        ) {
          this.fetchPosts();
        }
      });
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 600;
    },
  },
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('resize', this.checkIfMobile);
  },
};
</script>

<style scoped>
.page-container {
  padding: 0px 10px 0px 10px;
}
/* Profile Section Styling */
.back-link-section {
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

.back-link {
  color: #ffffff;
  text-decoration: none;
  font-weight: bold;
}

.arrow {
  font-size: 1.5em;
  margin-right: 8px;
  vertical-align: middle;
}

.profile-container {
  max-width: 560px;
  margin: 0px auto;
  padding: 20px;
  background-color: #192734;
  border-radius: 16px;
  color: #d9d9d9;
  text-align: left;
}

.profile-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
}

.profile-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
}

.profile-info h2 {
  margin: 0;
  color: #1da1f2;
}

.profile-info p {
  margin: 5px 0;
}

.bio {
  color: #8899a6;
}

.profile-stats {
  display: flex;
  justify-content: space-between;
  margin-left: 20px;
  margin-right: 20px;
}

.stat span {
  color: #1da1f2;
  font-size: 1.8em;
}

.stat p {
  color: #8899a6;
  margin-top: 2px;
}

.edit-profile-button {
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 10px 20px;
  border-radius: 24px;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  text-decoration: none;
}

.edit-profile-button:hover {
  background-color: #1a91da;
  transform: scale(1.05);
}

/* Toggle Buttons for Posts vs. Comments vs. Paid Tips */
.toggle-buttons {
  display: flex;
  justify-content: center;
  margin: 20px auto;
  gap: 10px;
}

.toggle-buttons button {
  background-color: transparent;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
}

.toggle-buttons button:hover {
  background-color: #1da1f2;
}

.toggle-buttons button.active {
  background-color: #15202b;
  border: 2px solid #1da1f2;
}

/* POSTS SECTION */
.timeline {
  max-width: 600px;
  margin: 0 auto;
  background-color: #15202b;
}

@media screen and (max-width: 600px) {
  .timeline {
    padding: 0px 0px 56px 0px;
  }
}

 /* TIPS SECTION */
 .comments-section,
 .tips-provided-section {
  max-width: 600px;
  margin: 0 auto;
  background-color: #15202b;
}

@media screen and (max-width: 600px) {
  .comments-section,
  .tips-provided-section {
    padding: 0px 4px 56px 4px;
  }
}

/* Sub-toggle buttons for Tips Provided section */
.sub-toggle-buttons {
  display: flex;
  justify-content: center;
  margin: 20px auto;
  gap: 10px;
  max-width: 400px;
}

.sub-toggle-buttons button {
  background-color: transparent;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.sub-toggle-buttons button:hover {
  background-color: #1da1f2;
}

.sub-toggle-buttons button.active {
  background-color: #192734;
  border: 2px solid #1da1f2;
}

/* Students section styling */
.students-section {
  max-width: 600px;
  margin: 0 auto;
}

.students-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.student-item {
  background-color: #192734;
  border-radius: 12px;
  overflow: hidden;
  transition: background-color 0.2s ease;
}

.student-item:hover {
  background-color: #1f2937;
}

.student-link {
  display: flex;
  align-items: center;
  padding: 16px;
  text-decoration: none;
  color: inherit;
  gap: 16px;
}

.student-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.student-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.student-username {
  color: #d9d9d9;
  font-weight: bold;
  font-size: 16px;
}

.student-tip-count {
  color: #1da1f2;
  font-size: 14px;
  font-weight: 500;
}

.no-students-message {
  text-align: center;
  padding: 40px 20px;
  background-color: #192734;
  border-radius: 12px;
  margin: 20px auto;
  max-width: 600px;
}

.no-students-message p {
  color: #d9d9d9;
  margin-bottom: 20px;
  font-size: 1.1em;
}

.tips-list {
  padding: 0px;
}

.students-list {
  padding: 0px;
  list-style: none;
}

/* Paid Tips Section Updates */
.paid-tips-list {
max-width: 600px;
margin: 0 auto; 
padding-inline-start: 0px;/* Center cards horizontally */
}

.payment-item {
display: flex;
flex-direction: column;
gap: 12px;
background-color: #192734;
margin-bottom: 10px;
padding: 12px;
max-width: 600px;
border-radius: 12px;
}

/* First row styling: Profile picture on left, status and price on right */
.payment-item .first-row {
display: flex;
justify-content: space-between;
align-items: center;
}

.pro-info {
display: flex;
align-items: center;
gap: 10px;
}

.pro-avatar {
width: 50px;
height: 50px;
border-radius: 50%;
object-fit: cover;
}

.payment-details {
display: flex;
flex-direction: column;
align-items: flex-end;
gap: 4px;
text-align: right;
}

.payment-status,
.payment-price {
font-weight: bold;
color: #1da1f2;
}

/* Second row styling: Video or content */
.payment-item .second-row {
display: flex;
align-items: center;
gap: 12px;
}

.post-info {
display: flex;
flex-direction: row;
align-items: center;
}

.paid-post-content {
  margin-left: 10px;
  max-width: 300px;
}

.post-thumbnail {
width: 100px;
height: 100px;
max-height: 200px;
object-fit: cover;
border-radius: 8px;
}

.post-content {
color: #d9d9d9;
text-align: center;
margin-top: 8px;
}

.post-media {
width: 100%;
max-height: 200px;
object-fit: cover;
border-radius: 8px;
}

.add-tip-form {
display: flex;
flex-direction: column;
gap: 8px;
margin-top: 8px;
}

.tip-textarea {
flex-grow: 1;
padding: 6px 10px;
border-radius: 15px;
border: 1px solid #38444d;
background-color: #192734;
color: #e1e8ed;
}

.submit-tip-btn {
padding: 6px 12px;
background-color: #1da1f2;
color: #ffffff;
border: none;
border-radius: 15px;
cursor: pointer;
}

.submit-tip-btn:hover {
background-color: #0c8ddb;
}

/* No tips message styling */
.no-tips-message {
  text-align: center;
  padding: 40px 20px;
  background-color: #192734;
  border-radius: 12px;
  margin: 20px auto;
  max-width: 600px;
}

.no-tips-message p {
  color: #d9d9d9;
  margin-bottom: 20px;
  font-size: 1.1em;
}

.request-tip-button {
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 24px;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
}

.request-tip-button:hover {
  background-color: #1a91da;
  transform: scale(1.05);
}

</style>
